import React from "react";
import { Tag } from "antd";
import * as Colors from "@unifiedpatents/euclid-react/lib/helpers/colors";
import { toDateString, toSetString, toYesNo } from "@/helpers/converters";
import { toString } from "lodash";
import { keepOnlyFieldQueryStrings, omitInvalidFieldSort, toByKey, toDataIndex, toQs2Label } from "@/helpers/field";
import { ENTITY_TYPE_MAP, renderEntityType } from "@/helpers/constants";

/**
 * Central field registry for the PTAB search page.
 *
 * Each entry describes one field with its core identity:
 *   key         – camelCase state key (null for query-only params like up_substatus, sort)
 *   field       – Elasticsearch / API field name (dot notation for nested)
 *   qs          – URL query-string key (null for column-only fields)
 *   label       – canonical display label (AppliedFilters tags, default column title)
 *   placeholder – filter placeholder text (omit if no filter)
 *   render      – shared display formatter for filters and default table rendering
 *
 * UI-specific config lives in the consuming components:
 *   toolbar.js                (panel grouping, options, mode, type, exact-match handling)
 *   table.js                  (width, isDefault, isExportOnly, title/render overrides)
 */

const INDUSTRY_MAP = {
  T: "High-Tech (T)",
  M: "Medical (M)",
  O: "Other (O)"
};

const TECH_CENTER_MAP = {
  1600: "1600: Biotechnology and Organic Chemistry",
  1700: "1700: Chemical and Materials Engineering",
  2100: "2100: Computer Architecture, Software, and Information Security",
  2400: "2400: Computer Networks, Multiplex communication, Video Distribution, and Security",
  2600: "2600: Communications",
  2800: "2800: Semiconductors, Electrical and Optical Systems and Components",
  2900: "2900: Designs",
  3600: "3600: Transportation,Construction, Electronic Commerce, Agriculture, National Security and License & Review",
  3700: "3700: Mechanical Engineering, Manufacturing, Products"
};

const DD_STATUTES_MAP = {
  "314A": "§ 314(a)",
  "325D": "§ 325(d)"
};

const PTAB_STATUS_TEXT_COLOR_MAP = {
  "Not Instituted - Merits": Colors.grey500,
  "Not Instituted - Procedural": Colors.grey500,
  "Procedural Termination": Colors.grey500,
  "Adverse Judgment": Colors.blue200,
  "Final Written Decision": Colors.blue500,
  Settlement: Colors.blue300,
  Pending: Colors.orange400,
  "Pending - Instituted": Colors.green400,
  Other: Colors.grey200
};

export const renderIndustry = value => (value ? INDUSTRY_MAP[value] : value);
export const renderTechCenter = value => (value ? TECH_CENTER_MAP[value] : value);
export const renderDDStatutes = value => (value ? DD_STATUTES_MAP[value] : value);
export const colorPTABStatus = value => (
  <Tag key="status" variant="solid" color={PTAB_STATUS_TEXT_COLOR_MAP[value]}>
    {value}
  </Tag>
);

export const FIELDS = [
  // --- Fields with both filter + column ---
  { key: "caseNumber", field: "case_number", qs: "case_number", label: "Case", placeholder: "e.g. IPR2012-00001", sorter: true },
  { key: "patentNumber", field: "patent.patent_number", qs: "patent.patent_number", label: "Patent", placeholder: "e.g. 8700996" },
  { key: "filingDate", field: "filing_date", qs: "filing_date", label: "Filing Date", render: toDateString, sorter: true },
  { key: "instDate", field: "up_institution_date", qs: "up_institution_date", label: "Inst. Date", render: toDateString, sorter: true },
  { key: "termDate", field: "up_termination_date", qs: "up_termination_date", label: "Term. Date", render: toDateString, sorter: true },
  { key: "petitioner", field: "petitioners", qs: "petitioners", label: "Petitioner", placeholder: "e.g. Unified Patents" },
  { key: "patentOwner", field: "patent_owners", qs: "patent_owners", label: "Owner", placeholder: "e.g. Uniloc" },
  { key: "techCenter", field: "ptab_tech_center", qs: "ptab_tech_center", label: "Tech Center", placeholder: "e.g. 2600", render: renderTechCenter },
  { key: "industry", field: "up_industry", qs: "up_industry", label: "Industry", placeholder: "e.g. High-Tech (T)", render: renderIndustry },
  { key: "poEntityType", field: "up_po_type", qs: "up_po_type", label: "P.O. Entity Type", placeholder: `e.g. ${ENTITY_TYPE_MAP.OC}`, render: renderEntityType },
  { key: "petEntityType", field: "up_pet_type", qs: "up_pet_type", label: "Pet. Entity Type", placeholder: `e.g. ${ENTITY_TYPE_MAP.OC_L}`, render: renderEntityType },
  { key: "petitionerLawfirm", field: "lawfirms.petitioner", qs: "lawfirms.petitioner", label: "Pet. Counsel", placeholder: "e.g. Finnegan", render: toSetString },
  { key: "patentOwnerLawfirm", field: "lawfirms.patent_owner", qs: "lawfirms.patent_owner", label: "P.O. Counsel", placeholder: "e.g. Sheppard", render: toSetString },
  { key: "authorJudge", field: "judges.author", qs: "judges.author", label: "Author Judge", placeholder: "e.g. Thomas L. Giannetti", render: toSetString },
  { key: "panelJudge", field: "judges.panel", qs: "judges.panel", label: "Panel Judge", placeholder: "e.g. Kevin F. Turner", render: toSetString },
  { key: "fullText", field: "filings.full_text", qs: "filings.full_text", label: "Full Text", placeholder: "e.g. troll" },
  { key: "filingType", field: "filings.up_type", qs: "filings.up_type", label: "Filing Type", placeholder: "e.g. Request for Rehearing" },
  { key: "caseType", field: "type", qs: "type", label: "Case Type", placeholder: "e.g. IPR" },
  { key: "claimOutcomeAtInstitution", field: "claim_outcome_at_institution", qs: "claim_outcome_at_institution", label: "Inst. Outcome", placeholder: "e.g. All Claims Instituted", render: toString },
  { key: "claimOutcomeAtFWD", field: "claim_outcome_at_fwd", qs: "claim_outcome_at_fwd", label: "FWD Outcome", placeholder: "e.g. All Claims Unpatentable", render: toString },
  { key: "isJoinedCase", field: "is_joined_case", qs: "is_joined_case", label: "Joined", placeholder: "e.g. true", render: toYesNo },
  { key: "ddStatutes", field: "discretionary_denial_statutes", qs: "discretionary_denial_statutes", label: "Discretionary Denial Statutes", placeholder: "e.g. § 314(a)", render: renderDDStatutes },
  { key: "ddRefs", field: "discretionary_denial_refs", qs: "discretionary_denial_refs", label: "§ 314(a) Grounds", placeholder: "e.g. NHK Spring / Fintiv" },
  { key: "errors", field: "errors", qs: "errors", label: "Potential Errors", placeholder: "e.g. Pending But Old" },

  // --- caseStatus: maps to up_status + up_substatus (split/join handled in toolbar) ---
  { key: "caseStatus", field: "up_status", qs: "up_status", label: "Case Status", placeholder: "e.g. Pending" },
  { key: null, field: "up_substatus", qs: "up_substatus", label: "Case Substatus" },
  { key: "casePhase", field: "up_institution_phase", qs: "up_institution_phase", label: "Case Phase", placeholder: "e.g. Post-Institution" },

  // --- Column-only (no filter UI) ---
  { key: "statusConcat", field: "up_status_concat", qs: null, label: "Status", render: colorPTABStatus, sorter: true },
  { key: "applNumber", field: "ptab_appl_number", qs: null, label: "Application" },

  // --- Export-only columns ---
  { key: "isOnAppeal", field: "is_on_appeal", qs: null, label: "On Appeal", render: toYesNo },
  { key: "isOnRemand", field: "is_on_remand", qs: null, label: "On Remand", render: toYesNo },

  // --- Query-only (not filter UI, not column) ---
  { key: null, field: "sort", qs: "sort", label: "Sort" },
];

// ---------- Derived lookups ----------

/** Key-indexed lookup: byKey["caseNumber"] → full field definition object */
export const byKey = toByKey(FIELDS);

/** QS-indexed label lookup for AppliedFilters and other PTAB consumers. */
export const qs2label = toQs2Label(FIELDS);

/** Keep only recognized PTAB query-string keys, omitting empty values. */
export const keepOnlyQueryStrings = query => keepOnlyFieldQueryStrings(FIELDS, query);

export const omitInvalidSort = query => omitInvalidFieldSort(FIELDS, query);

/** Convert ES field name to Ant Design dataIndex (splits dot notation). */
export { toDataIndex };